/**
 * Minimal XLSX writer - no dependencies.
 *
 * An .xlsx file is a ZIP archive of OOXML parts. We only ever need to write a single
 * sheet of plain values, so instead of pulling in a full spreadsheet library we emit
 * the handful of parts Excel requires and pack them with STORED (uncompressed) ZIP
 * entries. Stored entries keep the packer to a CRC32 and a few headers; the trade-off
 * is a larger file, which is a non-issue for a table export.
 *
 * Supports one sheet, a bold frozen header row, auto-filter and sized columns.
 * Values are written as text, number or boolean - no formulas, dates or styling.
 */

export type CellValue = string | number | boolean | null | undefined;

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const MAIN_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const PKG_REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

/** cellXfs index of the bold format declared in styles.xml */
const HEADER_STYLE = 1;

/** XML 1.0 forbids most C0 controls; Excel refuses to open a file that contains them. */
// eslint-disable-next-line no-control-regex
const ILLEGAL_XML_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

/** Excel rejects these in a sheet name, and caps the name at 31 characters. */
const FORBIDDEN_SHEET_CHARS = /[\\/*?:[\]]/g;

const escapeXml = (value: string): string =>
  value
    .replace(ILLEGAL_XML_CHARS, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

/** 0 -> "A", 25 -> "Z", 26 -> "AA" */
export const columnName = (index: number): string => {
  let name = '';
  let remaining = index;
  while (remaining >= 0) {
    name = String.fromCharCode(65 + (remaining % 26)) + name;
    remaining = Math.floor(remaining / 26) - 1;
  }
  return name;
};

// --- ZIP packing ---

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let value = i;
    for (let bit = 0; bit < 8; bit++) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

/** ZIP stores the timestamp as two packed 16 bit DOS fields, with a 1980 epoch. */
const dosTimestamp = (moment: Date) => ({
  time: (moment.getHours() << 11) | (moment.getMinutes() << 5) | Math.floor(moment.getSeconds() / 2),
  date: ((Math.max(moment.getFullYear(), 1980) - 1980) << 9) | ((moment.getMonth() + 1) << 5) | moment.getDate()
});

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

const buildZip = (entries: ZipEntry[], modified: Date, mimeType: string): Blob => {
  const encoder = new TextEncoder();
  const names = entries.map(entry => encoder.encode(entry.name));
  const { time, date } = dosTimestamp(modified);

  const localSize = entries.reduce((total, entry, i) => total + 30 + names[i]!.length + entry.data.length, 0);
  const centralSize = entries.reduce((total, _entry, i) => total + 46 + names[i]!.length, 0);

  const buffer = new ArrayBuffer(localSize + centralSize + 22);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  const localOffsets: number[] = [];
  const checksums: number[] = [];
  let offset = 0;

  // Local file headers, each followed by its (uncompressed) payload
  entries.forEach((entry, i) => {
    const name = names[i]!;
    const checksum = crc32(entry.data);
    localOffsets.push(offset);
    checksums.push(checksum);

    view.setUint32(offset, 0x04034b50, true);              // signature
    view.setUint16(offset + 4, 20, true);                  // version needed
    view.setUint16(offset + 6, 0x0800, true);              // flags: UTF-8 names
    view.setUint16(offset + 8, 0, true);                   // method: stored
    view.setUint16(offset + 10, time, true);
    view.setUint16(offset + 12, date, true);
    view.setUint32(offset + 14, checksum, true);
    view.setUint32(offset + 18, entry.data.length, true);  // compressed size
    view.setUint32(offset + 22, entry.data.length, true);  // uncompressed size
    view.setUint16(offset + 26, name.length, true);
    view.setUint16(offset + 28, 0, true);                  // no extra field
    bytes.set(name, offset + 30);
    bytes.set(entry.data, offset + 30 + name.length);
    offset += 30 + name.length + entry.data.length;
  });

  // Central directory
  const centralStart = offset;
  entries.forEach((entry, i) => {
    const name = names[i]!;
    view.setUint32(offset, 0x02014b50, true);              // signature
    view.setUint16(offset + 4, 20, true);                  // version made by
    view.setUint16(offset + 6, 20, true);                  // version needed
    view.setUint16(offset + 8, 0x0800, true);              // flags: UTF-8 names
    view.setUint16(offset + 10, 0, true);                  // method: stored
    view.setUint16(offset + 12, time, true);
    view.setUint16(offset + 14, date, true);
    view.setUint32(offset + 16, checksums[i]!, true);
    view.setUint32(offset + 20, entry.data.length, true);
    view.setUint32(offset + 24, entry.data.length, true);
    view.setUint16(offset + 28, name.length, true);
    view.setUint16(offset + 30, 0, true);                  // extra length
    view.setUint16(offset + 32, 0, true);                  // comment length
    view.setUint16(offset + 34, 0, true);                  // disk number
    view.setUint16(offset + 36, 0, true);                  // internal attributes
    view.setUint32(offset + 38, 0, true);                  // external attributes
    view.setUint32(offset + 42, localOffsets[i]!, true);
    bytes.set(name, offset + 46);
    offset += 46 + name.length;
  });

  // End of central directory
  view.setUint32(offset, 0x06054b50, true);
  view.setUint16(offset + 4, 0, true);                     // this disk
  view.setUint16(offset + 6, 0, true);                     // disk with central directory
  view.setUint16(offset + 8, entries.length, true);
  view.setUint16(offset + 10, entries.length, true);
  view.setUint32(offset + 12, offset - centralStart, true);
  view.setUint32(offset + 16, centralStart, true);
  view.setUint16(offset + 20, 0, true);                    // comment length

  return new Blob([buffer], { type: mimeType });
};

// --- OOXML parts ---

const cellXml = (reference: string, value: CellValue, styleIndex: number): string => {
  if (value === null || value === undefined || value === '') return '';
  const style = styleIndex ? ` s="${styleIndex}"` : '';

  if (typeof value === 'number') {
    // NaN/Infinity have no numeric representation in the format; drop the cell.
    return Number.isFinite(value) ? `<c r="${reference}"${style}><v>${value}</v></c>` : '';
  }
  if (typeof value === 'boolean') {
    return `<c r="${reference}"${style} t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  // Inline strings keep us from having to build and cross-reference a shared string table.
  return `<c r="${reference}"${style} t="inlineStr"><is><t xml:space="preserve">${escapeXml(String(value))}</t></is></c>`;
};

/** Widths are approximated from the longest value, then clamped so one long comment
 * cannot push a column off the screen. */
const colsXml = (headers: string[], rows: CellValue[][]): string => {
  if (headers.length === 0) return '';
  const columns = headers.map((header, index) => {
    let longest = String(header).length;
    for (const row of rows) {
      const value = row[index];
      if (value === null || value === undefined) continue;
      longest = Math.max(longest, String(value).length);
    }
    const width = Math.min(Math.max(longest + 2, 10), 50);
    return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
  });
  return `<cols>${columns.join('')}</cols>`;
};

const sheetXml = (headers: string[], rows: CellValue[][]): string => {
  const headerCells = headers.map((header, i) => cellXml(`${columnName(i)}1`, header, HEADER_STYLE)).join('');
  const bodyRows = rows
    .map((row, rowIndex) => {
      const rowNumber = rowIndex + 2;
      const cells = row.map((value, i) => cellXml(`${columnName(i)}${rowNumber}`, value, 0)).join('');
      return `<row r="${rowNumber}">${cells}</row>`;
    })
    .join('');

  const lastColumn = columnName(Math.max(headers.length - 1, 0));
  const lastRow = rows.length + 1;

  return (
    XML_HEADER +
    `<worksheet xmlns="${MAIN_NS}">` +
    `<dimension ref="A1:${lastColumn}${lastRow}"/>` +
    `<sheetViews><sheetView workbookViewId="0">` +
    `<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>` +
    `</sheetView></sheetViews>` +
    colsXml(headers, rows) +
    `<sheetData><row r="1">${headerCells}</row>${bodyRows}</sheetData>` +
    `<autoFilter ref="A1:${lastColumn}${lastRow}"/>` +
    `</worksheet>`
  );
};

const stylesXml = (): string =>
  XML_HEADER +
  `<styleSheet xmlns="${MAIN_NS}">` +
  `<fonts count="2">` +
  `<font><sz val="11"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="11"/><name val="Calibri"/></font>` +
  `</fonts>` +
  // Excel expects these first two fills to exist, in this order, even when unused.
  `<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>` +
  `<borders count="1"><border/></borders>` +
  `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
  `<cellXfs count="2">` +
  `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
  `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  `</cellXfs>` +
  `</styleSheet>`;

const workbookXml = (sheetName: string): string =>
  XML_HEADER +
  `<workbook xmlns="${MAIN_NS}" xmlns:r="${REL_NS}">` +
  `<sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets>` +
  `</workbook>`;

const contentTypesXml = (): string =>
  XML_HEADER +
  `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
  `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
  `<Default Extension="xml" ContentType="application/xml"/>` +
  `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
  `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
  `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
  `</Types>`;

const rootRelsXml = (): string =>
  XML_HEADER +
  `<Relationships xmlns="${PKG_REL_NS}">` +
  `<Relationship Id="rId1" Type="${REL_NS}/officeDocument" Target="xl/workbook.xml"/>` +
  `</Relationships>`;

const workbookRelsXml = (): string =>
  XML_HEADER +
  `<Relationships xmlns="${PKG_REL_NS}">` +
  `<Relationship Id="rId1" Type="${REL_NS}/worksheet" Target="worksheets/sheet1.xml"/>` +
  `<Relationship Id="rId2" Type="${REL_NS}/styles" Target="styles.xml"/>` +
  `</Relationships>`;

export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Builds a single sheet workbook as a Blob ready to be downloaded.
 *
 * @param headers  the header row; also fixes the column count
 * @param rows     one array of values per row, aligned to `headers`
 * @param sheetName tab name; sanitised to what Excel accepts
 */
export const buildXlsx = (
  headers: string[],
  rows: CellValue[][],
  sheetName = 'Sheet1',
  modified: Date = new Date()
): Blob => {
  const safeSheetName = sheetName.replace(FORBIDDEN_SHEET_CHARS, ' ').trim().slice(0, 31) || 'Sheet1';
  const encoder = new TextEncoder();
  const parts: Array<[string, string]> = [
    ['[Content_Types].xml', contentTypesXml()],
    ['_rels/.rels', rootRelsXml()],
    ['xl/workbook.xml', workbookXml(safeSheetName)],
    ['xl/_rels/workbook.xml.rels', workbookRelsXml()],
    ['xl/styles.xml', stylesXml()],
    ['xl/worksheets/sheet1.xml', sheetXml(headers, rows)]
  ];

  return buildZip(
    parts.map(([name, xml]) => ({ name, data: encoder.encode(xml) })),
    modified,
    XLSX_MIME_TYPE
  );
};
